import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface PhotoAnalysisResult {
  setting?: string;
  activities?: string[];
  style?: string;
  conversationTopics?: string[];
  observations?: string[];
}

export interface AIChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  modelUsed?: string;
  tokensUsed?: number;
  photoAnalysis?: PhotoAnalysisResult;
  createdAt: string;
}

export interface AIChatSession {
  id: string;
  contextType: 'dashboard' | 'feed_profile' | 'messaging' | 'connections' | 'general';
  contextProfileId?: string;
  title?: string;
  summary?: string;
  messageCount?: number;
  lastMessageAt?: string;
  createdAt: string;
}

export interface ProfileContext {
  profileId: string;
  displayName: string;
  age?: number;
  bio?: string;
  lookingFor?: string;
  photos?: Array<{ url: string; scanStatus?: string }>;
  occupation?: string;
}

interface UseAIChatOptions {
  contextType?: 'dashboard' | 'feed_profile' | 'messaging' | 'connections' | 'general';
  contextProfileId?: string;
  contextMatchId?: string;
  analyzePhotos?: boolean;
}

interface SendMessageParams {
  message: string;
  sessionId?: string;
}

export function useAIChat(options: UseAIChatOptions = {}) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const apiUrl = import.meta.env.VITE_API_URL || '';

  const [sessionId, setSessionId] = useState<string | undefined>();
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [photoAnalysis, setPhotoAnalysis] = useState<PhotoAnalysisResult | undefined>();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch session details if sessionId is set
  const { data: sessionData } = useQuery({
    queryKey: ['ai-chat-session', sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      const token = await getToken();
      const res = await fetch(`${apiUrl}/api/ai-chat/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch session');
      return res.json();
    },
    enabled: !!sessionId,
  });

  // Load messages from session data
  const loadedMessages = sessionData?.messages || [];

  // Non-streaming message send
  const sendMessageMutation = useMutation({
    mutationFn: async ({ message, sessionId: existingSessionId }: SendMessageParams) => {
      const token = await getToken();
      const res = await fetch(`${apiUrl}/api/ai-chat/message`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          sessionId: existingSessionId || sessionId,
          contextType: options.contextType || 'general',
          contextProfileId: options.contextProfileId,
          contextMatchId: options.contextMatchId,
          analyzePhotos: options.analyzePhotos,
        }),
      });
      if (!res.ok) throw new Error('Failed to send message');
      return res.json();
    },
    onSuccess: (data) => {
      if (data.sessionId && !sessionId) {
        setSessionId(data.sessionId);
      }
      if (data.photoAnalysis) {
        setPhotoAnalysis(data.photoAnalysis);
      }
      // Invalidate session query to refresh messages
      queryClient.invalidateQueries({ queryKey: ['ai-chat-session', data.sessionId] });
    },
  });

  // Streaming message send
  const sendMessageStreaming = useCallback(async (message: string) => {
    const token = await getToken();

    // Add user message to local state immediately
    const userMessage: AIChatMessage = {
      id: `temp-user-${Date.now()}`,
      role: 'user',
      content: message,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    // Start streaming
    setIsStreaming(true);
    setStreamingContent('');

    abortControllerRef.current = new AbortController();

    // Use dashboard endpoint for dashboard context
    const isDashboard = options.contextType === 'dashboard';
    const endpoint = isDashboard
      ? `${apiUrl}/api/ai-chat/dashboard/message/stream`
      : `${apiUrl}/api/ai-chat/message/stream`;

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: isDashboard
          ? JSON.stringify({ message, sessionId })
          : JSON.stringify({
              message,
              sessionId,
              contextType: options.contextType || 'general',
              contextProfileId: options.contextProfileId,
              contextMatchId: options.contextMatchId,
              analyzePhotos: options.analyzePhotos,
            }),
        signal: abortControllerRef.current.signal,
      });

      if (!res.ok) throw new Error('Failed to start stream');
      if (!res.body) throw new Error('No response body');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let messageId = '';
      let newSessionId = '';
      let buffer = ''; // Buffer for incomplete SSE messages

      // Helper function to process SSE messages
      const processSSEMessage = (message: string) => {
        if (!message.trim()) return;

        const lines = message.split('\n');
        let eventType = '';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6);
          }
        }

        if (eventType && eventData) {
          try {
            const data = JSON.parse(eventData);

            if (eventType === 'delta' && data.content) {
              fullContent += data.content;
              setStreamingContent(fullContent);
            } else if (eventType === 'photo_analysis' && data.photoAnalysis) {
              setPhotoAnalysis(data.photoAnalysis);
            } else if (eventType === 'done') {
              messageId = data.messageId;
              newSessionId = data.sessionId;
            } else if (eventType === 'error') {
              throw new Error(data.error || 'Stream error');
            }
          } catch (parseError) {
            console.warn('[AI Chat] Failed to parse SSE data:', eventData, parseError);
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Append new chunk to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (separated by double newlines)
        const messages = buffer.split('\n\n');

        // Keep the last potentially incomplete message in the buffer
        buffer = messages.pop() || '';

        for (const message of messages) {
          processSSEMessage(message);
        }
      }

      // Process any remaining content in the buffer after stream ends
      if (buffer.trim()) {
        processSSEMessage(buffer);
      }

      // Add assistant message to local state
      const assistantMessage: AIChatMessage = {
        id: messageId || `temp-assistant-${Date.now()}`,
        role: 'assistant',
        content: fullContent,
        createdAt: new Date().toISOString(),
        photoAnalysis,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      if (newSessionId && !sessionId) {
        setSessionId(newSessionId);
      }

    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log('[AI Chat] Stream aborted');
      } else {
        console.error('[AI Chat] Streaming error:', error);
        // Add error message
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try again.',
            createdAt: new Date().toISOString(),
          },
        ]);
      }
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
      abortControllerRef.current = null;
    }
  }, [getToken, apiUrl, sessionId, options, photoAnalysis]);

  // Cancel streaming
  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Clear chat and start fresh
  const clearChat = useCallback(() => {
    setSessionId(undefined);
    setMessages([]);
    setPhotoAnalysis(undefined);
    setStreamingContent('');
    setIsStreaming(false);
  }, []);

  // End session (mark as closed on server) and clear local state
  const endSession = useCallback(async () => {
    if (sessionId) {
      try {
        const token = await getToken();
        await fetch(`${apiUrl}/api/ai-chat/sessions/${sessionId}/end`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.warn('[AI Chat] Failed to end session:', error);
      }
    }
    // Clear local state regardless
    setSessionId(undefined);
    setMessages([]);
    setPhotoAnalysis(undefined);
    setStreamingContent('');
    setIsStreaming(false);
  }, [sessionId, getToken, apiUrl]);

  // Use local messages during active session, only use loadedMessages for session restoration
  // Local messages take priority since they include real-time streaming updates
  const allMessages = messages.length > 0 ? messages : loadedMessages;

  return {
    // State
    sessionId,
    messages: allMessages,
    isLoading: sendMessageMutation.isPending,
    isStreaming,
    streamingContent,
    photoAnalysis,
    profileContext: sessionData?.profileContext as ProfileContext | undefined,
    error: sendMessageMutation.error,

    // Actions
    sendMessage: sendMessageMutation.mutate,
    sendMessageStreaming,
    cancelStream,
    clearChat,
    endSession,
    setSessionId,
  };
}
